import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import type { GroupedMetrics, MetricLabel } from '../../domain/preferences';
import { useCompanyQuery } from '../company/useCompanyQuery';

import { preferencesApi } from './api';
import { preferencesQueryKey } from './preferencesQueryKey';

const fetchMetricsLabels = async (): Promise<MetricLabel[]> => preferencesApi.getMetricsLabels();

export const useMetricsLabelsQuery = () => {
  const { domain } = useCompanyQuery().data ?? {};

  return useQuery<MetricLabel[]>({
    queryKey: preferencesQueryKey.metricsLabels(),
    queryFn: fetchMetricsLabels,
    enabled: !!domain,
  });
};

export const useEngagementMetrics = () => {
  const { data: metricsLabels } = useMetricsLabelsQuery();

  return metricsLabels?.filter((label) => label.header === 'Engagement')?.map((label) => label.metric) ?? [];
};

export const useGroupedMetrics = () => {
  const { data: labels } = useMetricsLabelsQuery();

  const groupedMetrics: GroupedMetrics = useMemo(
    () =>
      labels?.reduce((groups: GroupedMetrics, item) => {
        const group = groups[item.header] || [];
        group.push(item);
        groups[item.header] = group;
        return groups;
      }, {}) || {},
    [labels]
  );

  return groupedMetrics;
};

export type MetricsPreferencesQuery = ReturnType<typeof useMetricsLabelsQuery>;
